import React, { useState, useEffect } from 'react';
import WindowedSelect from 'react-windowed-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LeaseApiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { FaSave, FaTrash } from 'react-icons/fa';
import { MdAdd } from 'react-icons/md';
import moment from 'moment';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';
import { useTranslation } from 'react-i18next';

// GetInvoiceDetailsByVoucherNo's DTOs are PascalCase in the backend but may
// serialize as camelCase - resolve each value against both instead of
// assuming one.
const pick = (row, keys, fallback = '') => {
    for (const k of keys) {
        const v = row?.[k];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return fallback;
};
const pickNum = (row, keys, fallback = 0) => {
    const v = pick(row, keys, null);
    return v === null ? fallback : Number(v) || 0;
};

const selectTheme = (theme) => ({ ...theme, borderRadius: 5, padding: 9 });
const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F5F5F5' : null,
        color: '#000',
        ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
    }),
};
const unitClassOptions = [
    { value: 'Lease', label: 'Lease' },
    { value: 'PM', label: 'PM' },
];

const emptyInvoiceHeader = {
    voucherNo: '', financialYear: '', amount: 0, currency: '', conRate: 1, fxAmount: 0,
    voucherType: '', invoiceDate: null, narration: '', amountReceived: 0, balanceDue: 0,
};
const emptyLine = () => ({ id: Date.now() + Math.random(), accountCode: null, accountName: null, credit: '', taxCode: null, vat: '', total: '', creditAmount: '', narration: '' });

// Maps one GetCommonData(Action=Edit) response onto the invoice header fields
// this page displays once a Voucher No is picked.
const mapInvoiceDetail = (data) => ({
    voucherNo: pick(data, ['voucherNo', 'VoucherNo']),
    financialYear: pick(data, ['financialYear', 'FinancialYear']),
    amount: pickNum(data, ['invoiceAmount', 'InvoiceAmount', 'amount', 'Amount', 'total', 'Total']),
    currency: pick(data, ['currency', 'Currency'], 'BHD'),
    conRate: pickNum(data, ['conRate', 'ConRate', 'conversionRate', 'ConversionRate'], 1),
    fxAmount: pickNum(data, ['fxAmount', 'FxAmount', 'convertedAmount', 'ConvertedAmount']),
    voucherType: pick(data, ['voucherType', 'VoucherType']),
    invoiceDate: pick(data, ['invoiceDate', 'InvoiceDate', 'postingDate', 'PostingDate']) || null,
    narration: pick(data, ['narration', 'Narration', 'commonNarration', 'CommonNarration']),
    amountReceived: pickNum(data, ['amountReceived', 'AmountReceived']),
    balanceDue: pickNum(data, ['balanceDue', 'BalanceDue']),
});
const mapInvoiceLines = (data) => {
    const raw = data?.lines || data?.Lines || data?.accountDetails || data?.AccountDetails || [];
    if (!raw.length) return [emptyLine()];
    return raw.map((l) => ({
        id: Date.now() + Math.random(),
        accountCode: { label: pick(l, ['accountCode', 'AccountCode', 'acCode', 'AcCode']), value: l },
        accountName: { label: pick(l, ['accountName', 'AccountName', 'acName', 'AcName']), value: l },
        credit: pickNum(l, ['creditAmount', 'CreditAmount']) || '',
        taxCode: { label: pick(l, ['taxCode', 'TaxCode', 'taxDescription', 'TaxDescription', 'tax', 'Tax'], 'None'), value: { Rate: pickNum(l, ['taxRate', 'TaxRate']) } },
        vat: pickNum(l, ['vat', 'Vat', 'VAT']),
        total: pickNum(l, ['total', 'Total']),
        creditAmount: pickNum(l, ['creditAmount', 'CreditAmount']),
        narration: pick(l, ['narration', 'Narration']),
    }));
};

const GeneralInvoiceReceiptListing = () => {
    const { t } = useTranslation(['leaseGeneralInvoice', 'common']);
    const apiService = new LeaseApiService();
    const { currencyPrecision } = useCurrency();

    // Search/select criteria - dropdowns loaded from the backend, cascading the
    // same way the desktop "Leasing - General Invoice" screen does
    // (UnitClass -> Property -> Unit -> Lease No, Tenant/TenantNo kept in sync).
    const [unitClass, setUnitClass] = useState(unitClassOptions[0]);
    const [property, setProperty] = useState(null);
    const [propertyList, setPropertyList] = useState([]);
    const [unit, setUnit] = useState(null);
    const [unitList, setUnitList] = useState([]);
    const [tenantName, setTenantName] = useState(null);
    const [tenantNo, setTenantNo] = useState(null);
    const [tenantList, setTenantList] = useState([]);
    const [leaseNo, setLeaseNo] = useState(null);
    const [leaseOptions, setLeaseOptions] = useState([]);
    const [voucherNo, setVoucherNo] = useState(null);
    const [voucherOptions, setVoucherOptions] = useState([]);

    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [invoice, setInvoice] = useState(emptyInvoiceHeader);
    const [lines, setLines] = useState([emptyLine()]);
    const [accountList, setAccountList] = useState([]);
    const [taxList, setTaxList] = useState([]);
    const [taxInclusive, setTaxInclusive] = useState(false);
    const [currencyList, setCurrencyList] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [voucherTypeList, setVoucherTypeList] = useState([]);
    const [selectedVoucherType, setSelectedVoucherType] = useState(null);

    const [receiptMethodList, setReceiptMethodList] = useState([]);
    const [receiptSaving, setReceiptSaving] = useState(false);
    const [receiptForm, setReceiptForm] = useState({ receiptDate: new Date(), amount: '', method: null, narration: '' });
    // Same lookup leaseGeneralInvoice.js uses for its own FinancialYear field -
    // GetInvoiceDetailsByVoucherNo requires an exact FinancialYear match and
    // the Voucher No dropdown (GIVoucherNo) doesn't carry one per voucher.
    const [activeFY, setActiveFY] = useState('');

    const fmt = (n) => Number(n || 0).toFixed(currencyPrecision?.decimalNumbers ?? 3);

    useEffect(() => {
        // Same resolution order as header.js's FY selector: whatever the user
        // explicitly picked (selectedFinancialYear) takes priority over the
        // isActive flag, so this stays in sync with the FY shown in the header.
        const savedFY = JSON.parse(localStorage.getItem('selectedFinancialYear'));
        if (savedFY?.name) {
            setActiveFY(savedFY.name);
            return;
        }
        const financialYears = JSON.parse(localStorage.getItem('financialYear')) || [];
        const activeFinancialYear = financialYears?.find((fy) => fy.isActive);
        if (activeFinancialYear) setActiveFY(activeFinancialYear.name);
    }, []);

    useEffect(() => {
        fetchDropdownData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchLeaseOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property, unit, tenantNo]);

    const fetchDropdownData = async () => {
        // Each call gets its own try/catch - these previously shared one block,
        // so a failure in an earlier call (e.g. the GET_GENERAL_MULTI dropdowns)
        // silently skipped every call after it, including the one that actually
        // populates Payment Method - it looked like Payment Method alone was
        // broken when the real failure could have been anywhere above it.
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LGISerPropertyName,LGITenantNames,LGIAccountNames,LGITransCurrency,LGIVoucherType');
            const res = await apiService.getApiParamater('GET_GENERAL_MULTI', `?${params.toString()}`);
            setPropertyList(res?.data?.dropdowns?.LGISerPropertyName || []);
            setTenantList(res?.data?.dropdowns?.LGITenantNames || []);
            setAccountList(res?.data?.dropdowns?.LGIAccountNames || []);
            setCurrencyList(res?.data?.dropdowns?.LGITransCurrency || []);
            setVoucherTypeList(res?.data?.dropdowns?.LGIVoucherType || []);
        } catch (error) {
            console.error('Error fetching property/tenant/account/currency/type dropdowns:', error);
        }

        try {
            const taxParams = new URLSearchParams();
            taxParams.append('Action', 'GetTaxDetails');
            const taxRes = await apiService.getApiParamater('TERMINATION_COMMON', `?${taxParams.toString()}`);
            setTaxList(taxRes?.data?.result?.data || []);
        } catch (error) {
            console.error('Error fetching tax list:', error);
        }

        await fetchReceiptMethods();
        await fetchVoucherOptions();
    };

    // Logged and parsed separately from the rest of fetchDropdownData - this is
    // a brand-new backend endpoint (GI_RECEIPT_RA_MULTI) whose exact response
    // shape isn't confirmed yet, so the dropdown key is resolved case-
    // insensitively (other RA endpoints in this app return lowercase keys,
    // e.g. unitMasterRA's "umproperties") instead of assuming "PaymentMethod"
    // exactly. If this still comes back empty, the raw response is logged so
    // the actual shape can be read from devtools and this can be corrected.
    const fetchReceiptMethods = async () => {
        try {
            const receiptParams = new URLSearchParams();
            receiptParams.append('dropdownTypes', 'PaymentMethod');
            const receiptRes = await apiService.getApiParamater('GI_RECEIPT_RA_MULTI', `?${receiptParams.toString()}`);
            const dropdowns = receiptRes?.data?.dropdowns || receiptRes?.data?.result?.dropdowns || {};
            const key = Object.keys(dropdowns).find((k) => k.toLowerCase() === 'paymentmethod');
            const list = key ? dropdowns[key] : [];
            if (!list.length) {
                console.warn('GI_RECEIPT_RA_MULTI returned no Payment Method options - raw response:', receiptRes?.data);
            }
            setReceiptMethodList(list);
        } catch (error) {
            console.error('Error fetching payment method dropdown (GI_RECEIPT_RA_MULTI):', error?.response?.data || error);
        }
    };

    // Voucher No isn't scoped to a Lease at all - it mirrors the legacy
    // desktop FillVoucherNo(), which lists every open (non-Completed)
    // LEASE-type voucher in tbPosting for the current financial year via the
    // "givoucherno" dropdown type on this same GI_RECEIPT_RA_MULTI endpoint.
    // (The previously-used GI_RECEIPT_RA_GET/"get" action is a different,
    // paginated invoice-search endpoint that doesn't accept a LeaseNo filter
    // at all and treats a literal PropertyNo/UnitNo=All as an exact-match
    // filter, so it always came back empty.)
    const fetchVoucherOptions = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'GIVoucherNo');
            const res = await apiService.getApiParamater('GI_RECEIPT_RA_MULTI', `?${params.toString()}`);
            const dropdowns = res?.data?.dropdowns || res?.data?.result?.dropdowns || {};
            const key = Object.keys(dropdowns).find((k) => k.toLowerCase() === 'givoucherno');
            const list = key ? dropdowns[key] : [];
            if (!list.length) {
                console.warn('GI_RECEIPT_RA_MULTI returned no Voucher No options - raw response:', res?.data);
            }
            setVoucherOptions(
                list.map((r) => {
                    const vNo = pick(r, ['value', 'Value', 'text', 'Text']);
                    return { value: { voucherNo: vNo }, label: vNo };
                }),
            );
        } catch (error) {
            console.error('Error fetching voucher no dropdown (GI_RECEIPT_RA_MULTI):', error?.response?.data || error);
            setVoucherOptions([]);
        }
    };

    // Same lease lookup already used by leaseGeneralInvoice.js's own search
    // popup (GetLGIPopUpSerLeasedetails) - filters by whichever of
    // Property/Unit/Tenant have been picked so far.
    const fetchLeaseOptions = async () => {
        try {
            const params = new URLSearchParams();
            params.append('Action', 'GetLGIPopUpSerLeasedetails');
            params.append('PropertyNo', property?.value?.value || 'All');
            params.append('UnitNo', unit?.label || 'All');
            params.append('TenantNo', tenantNo?.label || 'All');
            const res = await apiService.getApiParamater('GET_GENERAL_COMMON', `?${params.toString()}`);
            const rows = (res?.data?.result?.data || []).filter(Boolean);
            setLeaseOptions(rows.map((row) => ({ value: row, label: row?.LeaseNo })));
        } catch (error) {
            console.error('Error fetching lease options:', error);
            setLeaseOptions([]);
        }
    };

    const handleUnitClassChange = (opt) => {
        setUnitClass(opt);
        setProperty(null); setUnit(null); setUnitList([]); setLeaseNo(null); setVoucherNo(null);
    };
    const handlePropertyChange = async (opt) => {
        setProperty(opt);
        setUnit(null); setLeaseNo(null); setVoucherNo(null);
        try {
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LGIPopUpUnitsByPropId');
            params.append('CascadValue', opt?.value?.value);
            const res = await apiService.getApiParamater('GET_GENERAL_CASCAD', `?${params.toString()}`);
            setUnitList(res?.data?.items || []);
        } catch (error) {
            console.error('Error fetching unit list:', error);
            setUnitList([]);
        }
    };
    const handleUnitChange = (opt) => { setUnit(opt); setLeaseNo(null); setVoucherNo(null); };
    const handleTenantNameChange = (opt) => {
        const matched = tenantList?.find((rec) => rec?.text === opt?.label);
        setTenantName(opt);
        setTenantNo(matched ? { label: matched?.value, value: matched } : null);
        setLeaseNo(null); setVoucherNo(null);
    };
    const handleTenantNoChange = (opt) => {
        const matched = tenantList?.find((rec) => rec?.value === opt?.label);
        setTenantNo(opt);
        setTenantName(matched ? { label: matched?.text, value: matched } : null);
        setLeaseNo(null); setVoucherNo(null);
    };

    // Lease selection no longer drives the Voucher No list (see
    // fetchVoucherOptions) - it still fills in Property/Unit/Tenant from the
    // picked lease row, same as before, just without the broken per-lease
    // voucher fetch.
    const handleLeaseChange = (opt) => {
        setLeaseNo(opt);
        setVoucherNo(null);
        const row = opt?.value;
        if (row) {
            const matchedProperty = propertyList?.find((rec) => rec?.text === row?.PropertyName);
            if (matchedProperty) setProperty({ label: matchedProperty?.text, value: matchedProperty });
            setUnit(row?.UnitNo ? { label: row.UnitNo, value: row.UnitNo } : null);
            setTenantNo(row?.TenantNo ? { label: row.TenantNo, value: row } : null);
            setTenantName(row?.Tenant ? { label: row.Tenant, value: row } : null);
        }
    };

    const handleVoucherChange = async (opt) => {
        setVoucherNo(opt);
        setReceiptForm({ receiptDate: new Date(), amount: '', method: null, narration: '' });
        if (!opt?.value?.voucherNo) {
            setInvoice(emptyInvoiceHeader);
            setLines([emptyLine()]);
            setSelectedCurrency(null);
            setSelectedVoucherType(null);
            return;
        }
        setLoadingInvoice(true);
        try {
            const params = new URLSearchParams();
            // "Edit" isn't a real action on this endpoint - the backend only
            // supports GetInvoiceDetailsByVoucherNo and GetReceiptsByVoucherNo,
            // and any other Action value 400s with "Unknown action".
            params.append('Action', 'GetInvoiceDetailsByVoucherNo');
            params.append('VoucherNo', opt.value.voucherNo);
            params.append('FinancialYear', activeFY || '');
            const res = await apiService.getApiParamater('GI_RECEIPT_RA_COMMON', `?${params.toString()}`);
            const data = res?.data?.result?.data;
            const mapped = data ? mapInvoiceDetail(data) : emptyInvoiceHeader;
            setInvoice(mapped);
            setLines(data ? mapInvoiceLines(data) : [emptyLine()]);

            const matchedCurrency = currencyList?.find((rec) => rec?.value === mapped.currency);
            setSelectedCurrency(matchedCurrency ? { label: matchedCurrency?.value, value: matchedCurrency } : (mapped.currency ? { label: mapped.currency, value: mapped.currency } : null));
            const matchedVoucherType = voucherTypeList?.find((rec) => rec?.value === mapped.voucherType);
            setSelectedVoucherType(matchedVoucherType ? { label: matchedVoucherType?.value, value: matchedVoucherType } : (mapped.voucherType ? { label: mapped.voucherType, value: mapped.voucherType } : null));
        } catch (error) {
            console.error('Error loading invoice detail:', error);
            toast.error(t('leaseGeneralInvoice:receiptSaveFailed'));
            setInvoice(emptyInvoiceHeader);
            setLines([emptyLine()]);
            setSelectedCurrency(null);
            setSelectedVoucherType(null);
        } finally {
            setLoadingInvoice(false);
        }
    };

    // Same credit/tax/vat/total calculation already used by
    // leaseGeneralInvoice.js's own editable grid.
    const recalcLine = (row, taxInclusiveFlag) => {
        const credit = parseFloat(row.credit || 0);
        const rate = row.taxCode?.value?.Rate || 0;
        let vat = 0, total = 0, creditAmount = 0;
        if (credit) {
            if (taxInclusiveFlag) {
                vat = (credit * rate) / 100;
                total = credit;
                creditAmount = credit - (credit * rate) / 100;
            } else {
                vat = (credit * rate) / 100;
                total = credit + vat;
                creditAmount = credit;
            }
        }
        return {
            ...row,
            vat: credit ? vat.toFixed(currencyPrecision?.decimalNumbers) : '',
            total: credit ? total.toFixed(currencyPrecision?.decimalNumbers) : '',
            creditAmount: credit ? creditAmount.toFixed(currencyPrecision?.decimalNumbers) : '',
        };
    };
    const handleLineChange = (index, key, value) => {
        setLines((prev) => {
            const updated = [...prev];
            let row = { ...updated[index] };
            if (key === 'accountCode' || key === 'accountName') {
                row.accountName = { label: value?.value?.text || '', value: value?.value || '' };
                row.accountCode = { label: value?.value?.value || '', value: value?.value || '' };
            } else if (key === 'taxCode') {
                row.taxCode = value;
                row = recalcLine(row, taxInclusive);
            } else if (key === 'credit') {
                row.credit = value;
                row = recalcLine(row, taxInclusive);
            } else {
                row[key] = value;
            }
            updated[index] = row;
            return updated;
        });
    };
    const handleAddLine = () => setLines((prev) => [...prev, emptyLine()]);
    const handleDeleteLine = (index) => setLines((prev) => prev.filter((_, i) => i !== index));

    const handleReceiptChange = (key, value) => setReceiptForm((prev) => ({ ...prev, [key]: value }));

    const handleSaveReceipt = async () => {
        const amount = parseFloat(receiptForm.amount);
        if (!invoice.voucherNo) {
            toast.warning(t('leaseGeneralInvoice:pleaseSelectLeaseNo'));
            return;
        }
        if (!amount || amount <= 0) {
            toast.warning(t('leaseGeneralInvoice:pleaseEnterAmount'));
            return;
        }
        if (amount > invoice.balanceDue) {
            toast.warning(t('leaseGeneralInvoice:receiptAmountExceedsBalance'));
            return;
        }
        if (!receiptForm.method?.label) {
            toast.warning(t('leaseGeneralInvoice:pleaseSelectPaymentMethod'));
            return;
        }
        setReceiptSaving(true);
        try {
            const body = {
                voucherNo: invoice.voucherNo,
                financialYear: invoice.financialYear,
                tenantNo: tenantNo?.label,
                receiptDate: moment(receiptForm.receiptDate).format("YYYY-MM-DDTHH:mm:ss"),
                amount,
                paymentMethod: receiptForm.method.label,
                narration: receiptForm.narration || "0",
            };
            const postRes = await apiService.post("GI_RECEIPT_RA_CREATE", body);
            if (postRes?.data?.statusCode === 200 || postRes?.data?.statusCode === 201) {
                toast.success(postRes?.data?.message || t('leaseGeneralInvoice:receiptSavedSuccess'));
                handleVoucherChange(voucherNo);
            } else {
                toast.warning(postRes?.data?.message);
            }
        } catch (error) {
            console.error('Error saving receipt:', error);
            toast.error(t('leaseGeneralInvoice:receiptSaveFailed'));
        } finally {
            setReceiptSaving(false);
        }
    };

    const linesTotal = lines.reduce((sum, l) => sum + parseFloat(l.total || 0), 0);
    const linesCredit = lines.reduce((sum, l) => sum + parseFloat(l.creditAmount || 0), 0);

    return (
        <div className='p-3'>
            {/* Search/select criteria - all backend-loaded dropdowns, cascading the
                same way the desktop screen's Tenant/TenantID/UnitClass/Property/
                UnitNo/LeaseNo fields do. */}
            <div className='border-box2 p-3' style={{ borderRadius: '8px' }}>
                <div className="row gy-2">
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:tenant')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles} isClearable
                            value={tenantName} onChange={handleTenantNameChange}
                            options={tenantList?.map((rec) => ({ value: rec, label: rec?.text }))} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('common:tenantNo', 'Tenant No')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles} isClearable
                            value={tenantNo} onChange={handleTenantNoChange}
                            options={tenantList?.map((rec) => ({ value: rec, label: rec?.value }))} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('common:unitClass', 'Unit Class')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} styles={selectStyles}
                            value={unitClass} onChange={handleUnitClassChange} options={unitClassOptions} />
                    </div>

                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:property')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles} isClearable
                            value={property} onChange={handlePropertyChange}
                            options={propertyList?.map((rec) => ({ value: rec, label: rec?.text }))} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:unitNo')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles} isClearable
                            value={unit} onChange={handleUnitChange}
                            options={unitList?.map((rec) => ({ value: rec, label: rec?.text }))} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:leaseNo')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles} isClearable
                            value={leaseNo} onChange={handleLeaseChange} options={leaseOptions} />
                    </div>

                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:columns.voucherNo')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles} isClearable
                            placeholder={t('common:search')} value={voucherNo} onChange={handleVoucherChange} options={voucherOptions} />
                    </div>
                    <div className="col-md-4 d-flex align-items-center">
                        {loadingInvoice && <ClipLoader color="#6e5199" size={20} />}
                    </div>
                </div>
            </div>

            {/* Invoice header - loaded from the selected Voucher No, matching the
                desktop screen's Amount/Currency/ConRate/FxAmount/Type/Date/
                Narration fields. Currency and Type are backend-loaded dropdowns
                like the desktop's cbCurrency/cbType (which stay enabled even in
                the desktop's own Edit mode) - the rest stay plain read-only
                display since they're computed/derived values. */}
            <div className='border-box2 mt-3 p-3' style={{ borderRadius: '8px' }}>
                <div className="row gy-2">
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:amountDr')}</label>
                        <input type="text" className="form-control" readOnly value={fmt(invoice.amount)} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:currency')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles}
                            value={selectedCurrency} onChange={setSelectedCurrency}
                            options={currencyList?.map((rec) => ({ value: rec, label: rec?.value }))} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:type')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles}
                            value={selectedVoucherType} onChange={setSelectedVoucherType}
                            options={voucherTypeList?.map((rec) => ({ value: rec, label: rec?.value }))} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:conRate')}</label>
                        <input type="text" className="form-control" readOnly value={fmt(invoice.conRate)} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:fxAmount')}</label>
                        <input type="text" className="form-control" readOnly value={fmt(invoice.fxAmount)} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('common:date')}</label>
                        <input type="text" className="form-control" readOnly value={invoice.invoiceDate ? moment(invoice.invoiceDate).format('DD-MMM-YYYY') : ''} />
                    </div>
                    <div className="col-12">
                        <label className="form-label">{t('leaseGeneralInvoice:narration')}</label>
                        <textarea className="form-control" rows={2} readOnly value={invoice.narration}></textarea>
                    </div>
                </div>
            </div>

            {/* Account grid - editable and addable, same as leaseGeneralInvoice.js's
                own transaction grid (Account Code/Name dropdowns from the backend,
                Credit/Tax drive the Vat/Total calc, Narration free text, rows can
                be added or removed). */}
            <div className='border-box2 mt-3 p-3' style={{ borderRadius: '8px' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <input className="form-check-input" type="checkbox" id="taxInclusive" checked={taxInclusive}
                        onChange={(e) => { setTaxInclusive(e.target.checked); setLines((prev) => prev.map((r) => recalcLine(r, e.target.checked))); }}
                        style={{ width: 18, height: 18 }} />
                    <label className="form-check-label" htmlFor="taxInclusive">{t('leaseGeneralInvoice:taxInclusive')}</label>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table className='table table-bordered gridshad mb-0'>
                        <thead className='dynoTable'>
                            <tr>
                                <th>{t('leaseGeneralInvoice:action')}</th>
                                <th>{t('leaseGeneralInvoice:accountCode')}</th>
                                <th>{t('leaseGeneralInvoice:accountName')}</th>
                                <th>{t('leaseGeneralInvoice:amount')}</th>
                                <th>{t('leaseGeneralInvoice:tax')}</th>
                                <th>{t('leaseGeneralInvoice:credit')}</th>
                                <th>{t('common:vat')}</th>
                                <th>{t('common:total')}</th>
                                <th>{t('leaseGeneralInvoice:narration')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((row, index) => (
                                <tr key={row.id}>
                                    <td className="text-center">
                                        <FaTrash size={18} style={{ cursor: 'pointer', color: 'red' }} onClick={() => handleDeleteLine(index)} />
                                    </td>
                                    <td style={{ minWidth: 180 }}>
                                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles}
                                            value={row.accountCode} onChange={(opt) => handleLineChange(index, 'accountCode', opt)}
                                            options={accountList?.map((rec) => ({ value: rec, label: rec?.value }))} />
                                    </td>
                                    <td style={{ minWidth: 200 }}>
                                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles}
                                            value={row.accountName} onChange={(opt) => handleLineChange(index, 'accountName', opt)}
                                            options={accountList?.map((rec) => ({ value: rec, label: rec?.text }))} />
                                    </td>
                                    <td><input className='p-1 form-control' value={row.credit} onChange={(e) => handleLineChange(index, 'credit', e.target.value)} /></td>
                                    <td style={{ minWidth: 180 }}>
                                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles}
                                            value={row.taxCode} onChange={(opt) => handleLineChange(index, 'taxCode', opt)}
                                            options={taxList?.map((rec) => ({ value: rec, label: rec?.Description }))} />
                                    </td>
                                    <td><input className='p-1 form-control' value={row.creditAmount} disabled /></td>
                                    <td><input className='p-1 form-control' value={row.vat} disabled /></td>
                                    <td><input className='p-1 form-control' value={row.total} disabled /></td>
                                    <td><input className='p-1 form-control' value={row.narration} onChange={(e) => handleLineChange(index, 'narration', e.target.value)} /></td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="fw-bold">
                                <td colSpan={5}>{t('common:total')}</td>
                                <td>{fmt(linesCredit)}</td>
                                <td></td>
                                <td>{fmt(linesTotal)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <div className="row p-0 m-0 mt-2">
                    <div className="mainbtn col-2 gap-2" onClick={handleAddLine} style={{ cursor: 'pointer' }}>
                        <MdAdd color="#fff" className="center mt-1 mx-2" />
                        <div className="center">{t('leaseGeneralInvoice:addNew')}</div>
                    </div>
                </div>
            </div>

            {/* This part doesn't exist on the desktop screen - it's the actual
                reason this page exists: recording a payment against the invoice
                loaded above. */}
            <div className='border-box2 mt-3 p-3' style={{ borderRadius: '8px' }}>
                <h6 className="fw-bold mb-3">{t('leaseGeneralInvoice:addReceipt')}</h6>
                <div className='d-flex justify-content-between boxs mb-3' style={{ borderRadius: '8px', padding: '10px' }}>
                    <div className='d-flex gap-2'>
                        <div className="totalhead center">{t('leaseGeneralInvoice:invoiceTotal')}</div>
                        <div className='field'>{fmt(invoice.amount)}</div>
                    </div>
                    <div className='d-flex gap-2'>
                        <div className="totalhead center">{t('leaseGeneralInvoice:columns.received')}</div>
                        <div className='field'>{fmt(invoice.amountReceived)}</div>
                    </div>
                    <div className='d-flex gap-2'>
                        <div className="totalhead center">{t('leaseGeneralInvoice:balanceDue')}</div>
                        <div className='field'>{fmt(invoice.balanceDue)}</div>
                    </div>
                </div>
                <div className="row gy-2">
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:receiptDate')}</label>
                        <DatePicker todayButton="Today" selected={receiptForm.receiptDate} onChange={(date) => handleReceiptChange("receiptDate", date)}
                            dateFormat="dd-MMM-yyyy" className="p-2 form-control w-100"
                            portalId="receipt-date-portal" popperClassName="react-datepicker-popper" />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:paymentMethod')}</label>
                        <WindowedSelect theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" menuShouldScrollIntoView={false} styles={selectStyles}
                            value={receiptForm.method} onChange={(opt) => handleReceiptChange("method", opt)}
                            options={receiptMethodList?.map((rec) => ({ value: rec, label: rec?.text }))} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">{t('leaseGeneralInvoice:receiptAmount')}</label>
                        <input type="number" className="form-control" value={receiptForm.amount} onChange={(e) => handleReceiptChange("amount", e.target.value)} />
                    </div>
                    <div className="col-12">
                        <label className="form-label">{t('leaseGeneralInvoice:narration')}</label>
                        <textarea className="form-control" rows={2} maxLength={950} value={receiptForm.narration}
                            onChange={(e) => handleReceiptChange("narration", e.target.value)} placeholder={t('leaseGeneralInvoice:narrationPlaceholder')} />
                    </div>
                </div>
                <div className="mt-3">
                    {receiptSaving === false ? (
                        <button className='mainbtn' type="button" onClick={handleSaveReceipt} disabled={!invoice.voucherNo}>
                            <FaSave className="center mt-1 mx-2" />
                            <div>{t('common:save')}</div>
                        </button>
                    ) : (
                        <button className='mainbtn' type="button"><ClipLoader color="#ffffff" size={20} /></button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GeneralInvoiceReceiptListing;
